import apiClient from './client';

export interface ConfigRow {
  key: string;
  category: string | null;
  value: Record<string, unknown>;
}

export type ViewMode = 'simplified' | 'advanced';

/** Wire payload for the admin user-management list / detail endpoints.
 *  Mirrors `AdminUserResponse` on the backend. `has_crypto_key`/`family_count`
 *  are denormalized counters the list view shows without N+1 calls. */
export interface AdminUser {
  id: number;
  email: string;
  full_name: string | null;
  is_active: boolean;
  is_admin: boolean;
  preferred_llm_provider: string | null;
  preferred_llm_model: string | null;
  view_mode: ViewMode;
  created_at: string;
  family_count: number;
  has_crypto_key: boolean;
}

export interface AdminUserCreatePayload {
  email: string;
  password: string;
  full_name?: string | null;
  is_active?: boolean;
  is_admin?: boolean;
  view_mode?: ViewMode;
}

export interface AdminUserUpdatePayload {
  full_name?: string | null;
  is_active?: boolean;
  is_admin?: boolean;
  view_mode?: ViewMode;
}

export interface ConfigExport {
  version: number;
  items: ConfigRow[];
}

export interface QdrantMigrateResult {
  collection: string;
  scanned: number;
  updated: number;
  skipped: number;
  dry_run: boolean;
}

export interface QdrantRechunkAllResult {
  dry_run: boolean;
  documents_total: number;
  processed: number;
  skipped: number;
  errors: Array<{ document_id: number; error: string }>;
}

/** Response shape for `POST /api/admin/pipeline/reconcile`.
 *  Each `*_reset` field is the count of OCR result rows the janitor
 *  forcibly transitioned to an `error` / `failed` state. */
export interface PipelineReconcileResult {
  summary_reset: number;
  classification_reset: number;
  ocr_reset: number;
  threshold_seconds: number;
}

// ---------------------------------------------------------------------------
// Classifiers admin API
//
// Classifiers are the admin-managed registry of document classification
// categories (e.g. document_type, specialty, imaging_modality). They
// supersede the previous hardcoded `FacetClassifier` enum and drive
// the LLM JSON schema, the value vocabulary (taxonomy tree + links),
// the filter UI and the display chips. See the
// `classifications-admin-page` plan for the full contract.
// ---------------------------------------------------------------------------

/** Cardinality of a classifier value:
 *  - `single`: at most one value per document.
 *  - `multi`:  any number of values per document. */
export type ClassifierCardinality = 'single' | 'multi';

/** Value source mode:
 *  - `taxonomy`: only values from the taxonomy tree are accepted; unknowns are dropped.
 *  - `freeform`: any LLM-emitted value is kept verbatim, no triage.
 *  - `mixed`:    taxonomy is preferred but unknowns are accepted + sent to the triage inbox. */
export type ClassifierValueSource = 'taxonomy' | 'freeform' | 'mixed';

/** Full admin view of a classifier — what the admin CRUD list returns. */
export interface Classifier {
  id: number;
  key: string;
  label_ro: string;
  label_en: string | null;
  description: string | null;
  json_path: string;
  cardinality: ClassifierCardinality;
  value_source: ClassifierValueSource;
  show_in_filters: boolean;
  show_in_card: boolean;
  show_in_detail: boolean;
  display_order: number;
  active: boolean;
  entry_count: number;
  pending_count: number;
  created_at: string;
  updated_at: string;
}

/** A single taxonomy value exposed publicly alongside its classifier.
 *  This is the minimum the UI needs to translate a raw code (what the
 *  LLM emits and what we persist) into its display label. Mirrors the
 *  subset of {@link TaxonomyEntry} that's safe to ship to non-admin
 *  callers — no IDs, no timestamps. */
export interface ClassifierEntryConfig {
  code: string;
  label_ro: string;
  label_en: string | null;
  /** Optional synonyms; surfaced for callers that want to do fuzzy
   *  label resolution on raw LLM output before falling back to the code. */
  aliases?: string[];
}

/** Subset of classifier fields exposed by GET /api/classifiers/config to
 *  any authenticated user — used by the runtime UI to render filters / chips. */
export interface ClassifierConfig {
  id: number;
  key: string;
  label_ro: string;
  label_en: string | null;
  cardinality: ClassifierCardinality;
  /** Backend may also expose `value_source` so the UI can decide whether to
   *  treat unknown codes as freeform pills or as taxonomy violations. */
  value_source?: ClassifierValueSource;
  show_in_filters: boolean;
  show_in_card: boolean;
  show_in_detail: boolean;
  display_order: number;
  /** Where this classifier's values live inside the raw LLM classification
   *  JSON (e.g. `document.document_types`, `panels[].panel`). When present,
   *  `ClassificationDisplay` walks this path to extract values generically
   *  instead of hardcoding which fields exist. Optional because older
   *  backends may not include it in the public config payload. */
  json_path?: string;
  /** Flat list of taxonomy entries the registry curates for this classifier.
   *  When present, the UI uses it to translate raw codes → display labels
   *  without an extra round-trip per chip. Optional because older backends
   *  may not embed entries in the public config payload. */
  entries?: ClassifierEntryConfig[];
}

export interface ClassifierCreatePayload {
  key: string;
  label_ro: string;
  label_en?: string | null;
  description?: string | null;
  json_path: string;
  cardinality: ClassifierCardinality;
  value_source: ClassifierValueSource;
  show_in_filters?: boolean;
  show_in_card?: boolean;
  show_in_detail?: boolean;
  display_order?: number;
  active?: boolean;
}

export interface ClassifierUpdatePayload {
  key?: string;
  label_ro?: string;
  label_en?: string | null;
  description?: string | null;
  json_path?: string;
  cardinality?: ClassifierCardinality;
  value_source?: ClassifierValueSource;
  show_in_filters?: boolean;
  show_in_card?: boolean;
  show_in_detail?: boolean;
  display_order?: number;
  active?: boolean;
}

/** Flat taxonomy entry shape (no children). Used in lists and selectors. */
export interface TaxonomyEntry {
  id: number;
  classifier_id: number;
  parent_id: number | null;
  code: string;
  label_ro: string;
  label_en: string | null;
  description: string | null;
  aliases: string[];
  sort_order: number;
  active: boolean;
  created_at: string;
  updated_at: string;
}

/** Recursive tree node returned by GET /classifiers/{id}/tree. */
export interface TaxonomyTreeNode extends TaxonomyEntry {
  children: TaxonomyTreeNode[];
}

export interface TaxonomyEntryCreatePayload {
  code: string;
  label_ro: string;
  label_en?: string | null;
  description?: string | null;
  aliases?: string[];
  parent_id?: number | null;
  sort_order?: number;
  active?: boolean;
}

export interface TaxonomyEntryUpdatePayload {
  code?: string;
  label_ro?: string;
  label_en?: string | null;
  description?: string | null;
  aliases?: string[];
  parent_id?: number | null;
  sort_order?: number;
  active?: boolean;
}

export type TaxonomyLinkType = 'implies' | 'related' | 'narrower';

/** Outgoing or incoming cross-classifier link between taxonomy entries. */
export interface TaxonomyLink {
  id: number;
  from_entry_id: number;
  to_entry_id: number;
  link_type: TaxonomyLinkType;
  /** Convenience hydration so the UI doesn't need a separate lookup. */
  to_entry?: TaxonomyEntry;
  to_classifier?: Classifier;
  created_at: string;
}

export type PendingFacetStatus = 'pending' | 'ignored';

/** A raw value the LLM emitted that didn't match the taxonomy. */
export interface PendingFacet {
  id: number;
  classifier_id: number;
  classifier_key?: string;
  classifier_label_ro?: string;
  value: string;
  value_normalized: string;
  occurrences_count: number;
  first_seen_at: string;
  last_seen_at: string;
  status: PendingFacetStatus;
}

/** POST /classifiers/pending/{id}/promote — turns a pending value into a
 *  new taxonomy entry on its classifier. `label_ro` defaults to the
 *  raw value on the backend if not supplied; the UI pre-fills it. */
export interface PendingFacetPromotePayload {
  parent_id?: number | null;
  code?: string;
  label_ro: string;
  label_en?: string | null;
  description?: string | null;
  aliases?: string[];
}

/** POST /classifiers/pending/{id}/merge — folds a pending value into an
 *  existing taxonomy entry. By default the raw value is appended as an
 *  alias on the target so future LLM emissions match canonically. */
export interface PendingFacetMergePayload {
  target_entry_id: number;
  add_as_alias?: boolean;
}

/** Per-row failure inside a bulk action — `pending_id` is the row that
 *  failed and `reason` is the human-readable explanation surfaced verbatim
 *  in the UI's expandable `<details>` block. */
export interface BulkActionError {
  pending_id: number;
  reason: string;
}

/** Response envelope for `POST /classifiers/pending/bulk-{ignore,merge,promote}`.
 *  Bulk endpoints process rows inside a single DB transaction on the
 *  backend; `processed` + `skipped` always sum to the input batch size,
 *  and `errors[]` contains a stable reference back to each failed row. */
export interface BulkActionResult {
  processed: number;
  skipped: number;
  errors: BulkActionError[];
}

/**
 * Response shape for `GET /api/admin/config/prompts/{key}/preview`.
 *
 * - `template` is the raw `$`-placeholder text (DB value, or in-code
 *   default when the DB row is missing).
 * - `rendered` is what the LLM (or chat surface, for `responses.*`)
 *   would actually receive — `template` with `safe_substitute` applied
 *   over `context`.
 * - `context` maps each placeholder to the value used in this preview
 *   (truncated to ~6 KB per value by the backend).
 * - `source`:
 *     - `"live"`  — classification prompts: substituted from the live
 *                   taxonomy (matches a real classify call exactly).
 *     - `"stubs"` — every other `prompts.*` key: realistic illustrative
 *                   values (real per-call data will differ).
 *     - `"static"` — `responses.*` strings: no substitution at runtime
 *                   either, so `rendered === template`.
 * - `is_template` is `false` for `responses.*`, `true` for `prompts.*`.
 */
export interface PromptPreview {
  key: string;
  template: string;
  rendered: string;
  rendered_chars: number;
  template_chars: number;
  context: Record<string, string>;
  source: 'live' | 'stubs' | 'static';
  is_template: boolean;
  default: string;
}

export const adminApi = {
  listConfig: async (): Promise<ConfigRow[]> => {
    const res = await apiClient.get('/api/admin/config');
    return res.data;
  },
  exportConfig: async (): Promise<ConfigExport> => {
    const res = await apiClient.get('/api/admin/config/export');
    return res.data;
  },
  importConfig: async (
    items: ConfigRow[],
    replace = false
  ): Promise<{ imported: number }> => {
    const res = await apiClient.post('/api/admin/config/import', { items, replace });
    return res.data;
  },
  putConfig: async (key: string, value: Record<string, unknown>): Promise<void> => {
    await apiClient.put(`/api/admin/config/${encodeURIComponent(key)}`, { value });
  },
  deleteConfig: async (key: string): Promise<void> => {
    await apiClient.delete(`/api/admin/config/${encodeURIComponent(key)}`);
  },
  deleteAllConfig: async (): Promise<{ deleted: number }> => {
    const res = await apiClient.delete('/api/admin/config');
    return res.data;
  },
  /**
   * Seed in-code defaults for every `system_config` key.
   *
   * `wipe=true` clears every existing config row first (audit-logged),
   * then re-inserts the defaults. Use after a non-additive key rename
   * to drop stale keys that no longer have a runtime reader.
   */
  seedDefaults: async (
    opts?: { wipe?: boolean; preset?: string }
  ): Promise<{ seeded: number; preset: string }> => {
    // Default to the `medical` overlay for this deployment: eDosarMedical
    // ships with `app.services.seeds.medical_preset` as the active domain.
    // Without it, the backend falls back to `core` and seeds only the
    // neutral baseline — `classification.base_skeleton`,
    // `responses.disclaimer.*`, `maps.synonyms`, `chat.focus_modes`,
    // `prompts.*` etc. would all end up empty / generic instead of
    // medical-flavored.
    const preset = opts?.preset ?? 'medical';
    const params: Record<string, string | boolean> = { preset };
    if (opts?.wipe) params.wipe = true;
    const res = await apiClient.post('/api/admin/seed-defaults', null, {
      params,
    });
    return res.data;
  },
  /**
   * Render any `prompts.*` / `responses.*` config value with all
   * `$`-placeholders substituted. Classification prompts use the live
   * taxonomy (exact LLM payload); everything else uses realistic stub
   * values so admins can validate template shape without firing a real
   * call.
   */
  previewPrompt: async (key: string): Promise<PromptPreview> => {
    const res = await apiClient.get(
      `/api/admin/config/prompts/${encodeURIComponent(key)}/preview`
    );
    return res.data;
  },
  qdrantMigrateLangchainPayload: async (opts?: {
    dry_run?: boolean;
    batch_size?: number;
    max_points?: number | null;
  }): Promise<QdrantMigrateResult> => {
    const res = await apiClient.post('/api/admin/qdrant/migrate-langchain-payload', {
      dry_run: opts?.dry_run ?? false,
      batch_size: opts?.batch_size ?? 256,
      max_points: opts?.max_points ?? null,
    });
    return res.data;
  },
  qdrantRechunkAll: async (opts?: {
    dry_run?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<QdrantRechunkAllResult> => {
    const res = await apiClient.post('/api/admin/qdrant/rechunk-all', {
      dry_run: opts?.dry_run ?? false,
      limit: opts?.limit ?? 0,
      offset: opts?.offset ?? 0,
    });
    return res.data;
  },

  /**
   * Force-clear OCR / summary / classification rows stuck in a transient
   * state (`processing`, `generating`, etc.). Calls the pipeline janitor
   * on the backend, which emits SSE error events so any open dossier
   * dismisses its spinner immediately.
   *
   * `threshold_seconds=0` flips every transient row regardless of age —
   * only safe right after a planned worker/ocr-service restart when you
   * KNOW no task is currently running. Omit (or pass `null`) to fall
   * back to `PIPELINE_STUCK_THRESHOLD_SECONDS` (default 30 minutes).
   */
  pipelineReconcile: async (
    thresholdSeconds: number | null = 0
  ): Promise<PipelineReconcileResult> => {
    const res = await apiClient.post('/api/admin/pipeline/reconcile', {
      threshold_seconds: thresholdSeconds,
    });
    return res.data;
  },

  // User management — admin-only. Self-service flows (profile edit,
  // password change) stay under /api/auth/*.
  users: {
    list: async (): Promise<AdminUser[]> => {
      const res = await apiClient.get('/api/admin/users');
      return res.data;
    },
    get: async (id: number): Promise<AdminUser> => {
      const res = await apiClient.get(`/api/admin/users/${id}`);
      return res.data;
    },
    create: async (payload: AdminUserCreatePayload): Promise<AdminUser> => {
      const res = await apiClient.post('/api/admin/users', payload);
      return res.data;
    },
    update: async (id: number, payload: AdminUserUpdatePayload): Promise<AdminUser> => {
      const res = await apiClient.patch(`/api/admin/users/${id}`, payload);
      return res.data;
    },
    resetPassword: async (id: number, newPassword: string): Promise<void> => {
      await apiClient.post(`/api/admin/users/${id}/reset-password`, {
        new_password: newPassword,
      });
    },
    remove: async (id: number): Promise<void> => {
      await apiClient.delete(`/api/admin/users/${id}`);
    },
  },

  // Classifiers + taxonomy + links + triage inbox.
  //
  // Endpoint group layout mirrors the plan's
  // "## Backend code changes -> ### API -> Admin endpoints" section:
  //   /api/admin/classifiers                 (list/create)
  //   /api/admin/classifiers/{id}            (get/patch/delete)
  //   /api/admin/classifiers/{id}/move       (reorder)
  //   /api/admin/classifiers/{id}/tree       (nested taxonomy)
  //   /api/admin/classifiers/{id}/entries    (create entry)
  //   /api/admin/classifiers/entries/{eid}   (patch/delete entry)
  //   /api/admin/classifiers/entries/{eid}/move
  //   /api/admin/classifiers/entries/{eid}/links (list/add)
  //   /api/admin/classifiers/links/{lid}     (remove)
  //   /api/admin/classifiers/pending         (list)
  //   /api/admin/classifiers/pending/{id}/{promote|merge|ignore}
  //
  // Everything here is admin-only on the backend; non-admin callers will
  // get a 403 and that bubbles up via the global axios interceptor.
  classifiers: {
    list: async (): Promise<Classifier[]> => {
      const res = await apiClient.get('/api/admin/classifiers');
      return res.data;
    },
    get: async (id: number): Promise<Classifier> => {
      const res = await apiClient.get(`/api/admin/classifiers/${id}`);
      return res.data;
    },
    create: async (payload: ClassifierCreatePayload): Promise<Classifier> => {
      const res = await apiClient.post('/api/admin/classifiers', payload);
      return res.data;
    },
    update: async (id: number, payload: ClassifierUpdatePayload): Promise<Classifier> => {
      const res = await apiClient.patch(`/api/admin/classifiers/${id}`, payload);
      return res.data;
    },
    /** DELETE cascades to taxonomy + links + pending + document_facets rows.
     *  Response surfaces the cascade counts so the UI can confirm/report. */
    remove: async (
      id: number
    ): Promise<{ cascaded_facets: number; cascaded_entries: number; cascaded_pending: number }> => {
      const res = await apiClient.delete(`/api/admin/classifiers/${id}`);
      return res.data;
    },
    move: async (id: number, displayOrder: number): Promise<Classifier> => {
      const res = await apiClient.post(`/api/admin/classifiers/${id}/move`, {
        display_order: displayOrder,
      });
      return res.data;
    },

    getTree: async (id: number): Promise<TaxonomyTreeNode[]> => {
      const res = await apiClient.get(`/api/admin/classifiers/${id}/tree`);
      return res.data;
    },
    createEntry: async (
      classifierId: number,
      payload: TaxonomyEntryCreatePayload
    ): Promise<TaxonomyEntry> => {
      const res = await apiClient.post(`/api/admin/classifiers/${classifierId}/entries`, payload);
      return res.data;
    },
    updateEntry: async (
      entryId: number,
      payload: TaxonomyEntryUpdatePayload
    ): Promise<TaxonomyEntry> => {
      const res = await apiClient.patch(`/api/admin/classifiers/entries/${entryId}`, payload);
      return res.data;
    },
    removeEntry: async (entryId: number): Promise<void> => {
      await apiClient.delete(`/api/admin/classifiers/entries/${entryId}`);
    },
    moveEntry: async (
      entryId: number,
      payload: { parent_id?: number | null; sort_order?: number }
    ): Promise<TaxonomyEntry> => {
      const res = await apiClient.post(`/api/admin/classifiers/entries/${entryId}/move`, payload);
      return res.data;
    },

    listLinks: async (entryId: number): Promise<TaxonomyLink[]> => {
      const res = await apiClient.get(`/api/admin/classifiers/entries/${entryId}/links`);
      return res.data;
    },
    addLink: async (
      entryId: number,
      payload: { to_entry_id: number; link_type?: TaxonomyLinkType }
    ): Promise<TaxonomyLink> => {
      const res = await apiClient.post(`/api/admin/classifiers/entries/${entryId}/links`, payload);
      return res.data;
    },
    removeLink: async (linkId: number): Promise<void> => {
      await apiClient.delete(`/api/admin/classifiers/links/${linkId}`);
    },

    listPending: async (filter?: {
      classifier_id?: number;
      status?: PendingFacetStatus | 'all';
    }): Promise<PendingFacet[]> => {
      const params: Record<string, string> = {};
      if (filter?.classifier_id != null) params.classifier_id = String(filter.classifier_id);
      if (filter?.status && filter.status !== 'all') params.status = filter.status;
      const res = await apiClient.get('/api/admin/classifiers/pending', {
        params: Object.keys(params).length ? params : undefined,
      });
      return res.data;
    },
    promotePending: async (
      pendingId: number,
      payload: PendingFacetPromotePayload
    ): Promise<TaxonomyEntry> => {
      const res = await apiClient.post(
        `/api/admin/classifiers/pending/${pendingId}/promote`,
        payload
      );
      return res.data;
    },
    mergePending: async (
      pendingId: number,
      payload: PendingFacetMergePayload
    ): Promise<{ updated_facets: number }> => {
      const res = await apiClient.post(
        `/api/admin/classifiers/pending/${pendingId}/merge`,
        payload
      );
      return res.data;
    },
    ignorePending: async (pendingId: number): Promise<PendingFacet> => {
      const res = await apiClient.post(`/api/admin/classifiers/pending/${pendingId}/ignore`);
      return res.data;
    },

    // Bulk endpoints — sibling of the single-row promote/merge/ignore.
    // Each one wraps the loop in a single DB transaction on the backend,
    // so a partial failure rolls everything back; the envelope reports
    // per-row outcomes. See `plans/netriajate-bulk-actions.md`.
    bulkIgnorePending: async (pendingIds: number[]): Promise<BulkActionResult> => {
      const res = await apiClient.post('/api/admin/classifiers/pending/bulk-ignore', {
        pending_ids: pendingIds,
      });
      return res.data;
    },
    /** Backend rejects with `400` if the selection spans multiple
     *  classifiers (defensive guard — the UI already prevents this). */
    bulkMergePending: async (
      pendingIds: number[],
      targetEntryId: number,
      addAsAlias: boolean
    ): Promise<BulkActionResult> => {
      const res = await apiClient.post('/api/admin/classifiers/pending/bulk-merge', {
        pending_ids: pendingIds,
        target_entry_id: targetEntryId,
        add_as_alias: addAsAlias,
      });
      return res.data;
    },
    /** `parent_id` is optional — when provided, the backend rejects with
     *  `400` unless every pending row belongs to the same classifier as
     *  the parent. Labels/codes are derived server-side via slugify. */
    bulkPromotePending: async (
      pendingIds: number[],
      parentId: number | null
    ): Promise<BulkActionResult> => {
      const res = await apiClient.post('/api/admin/classifiers/pending/bulk-promote', {
        pending_ids: pendingIds,
        parent_id: parentId,
      });
      return res.data;
    },
  },
};
